import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Warmup — connect a mailbox → ramp → per-ESP reputation → warmed up at 80.
 * Grounded in the warmup-inbox code (WarmupInboxService.create, defaultFrequencyFor tier constants,
 * WarmupSyncService thresholds WARMED_UP_THRESHOLD=80 / DROP_THRESHOLD=10, warmup_inbox.* events).
 * The send/reply network and the raw score math run inside the warmup engine; SieveGuard owns the
 * connect, the ramp parameters, the tier gating, the daily reputation sync, and the webhooks — the
 * copy stays at that product level and never claims our servers run the partner loop.
 */

const STAGES = ['Connect', 'Start', 'Ramp', 'Tune', 'Reputation', 'Warmed up'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Connect',
    title: 'Wire in a mailbox',
    body: (
      <>
        Click <span className="font-mono">Continue with Google</span>, paste Microsoft tokens, or drop SMTP/IMAP
        credentials — CSV bulk-connect for a fleet. Each mailbox becomes one row, starting{' '}
        <span className="font-mono">paused</span> at 0 reputation until you start it.
      </>
    ),
    facts: ['Google · Microsoft · SMTP', 'CSV bulk-connect', 'starts paused'],
    detail: (
      <StageCard head="Connect a mailbox">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">sarah@acme.co · Google OAuth</div>
          <div className="text-allow">→ inbox row · status paused · rep 0</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          offline consent → refresh token stored
        </div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'Start',
    title: 'The ramp begins',
    body: (
      <>
        Hit start and the mailbox flips to <span className="font-mono">running</span>. Volume opens at a{' '}
        <span className="font-mono">starting_baseline</span> of 0 and climbs{' '}
        <span className="font-mono">+2 sends/day</span> — the engine&rsquo;s own recommended gentle ramp, never a cold
        blast.
      </>
    ),
    facts: ['status → running', 'baseline 0', '+2 / day'],
    detail: (
      <StageCard head="Ramp schedule">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <Kv k="baseline" v="0" />
          <Kv k="+ / day" v="2" />
          <Kv k="strategy" v="progressive" />
          <Kv k="status" v="running" hot />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">weekly send windows configurable</div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Ramp',
    title: 'Climb to the tier ceiling',
    body: (
      <>
        Daily volume rises toward the ceiling of the tier you chose for that mailbox — 30, 50 or 75 warmup sends a day —
        at matching reply rates of 25%, 45% and 65%. Mix a light ramp on one mailbox with a heavy one on another.
      </>
    ),
    facts: ['30 · 50 · 75 sends/day', 'reply 25 · 45 · 65%', 'per-mailbox tier'],
    detail: (
      <StageCard head="Tier ceilings">
        <div className="space-y-1 font-mono text-[10.5px] md:text-[11.5px]">
          <TierRow tier="Starter" max="30/day" reply="25%" />
          <TierRow tier="Growth" max="50/day" reply="45%" active />
          <TierRow tier="Scale" max="75/day" reply="65%" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">reply-rate ceiling enforced by tier</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Tune',
    title: 'Content that looks real',
    body: (
      <>
        Growth and Scale unlock the advanced controls: AI warmup content in 23 languages, warmup topics, ESP priority to
        bias toward Google or Microsoft, scheduling, and your own templates. The AI is prompted to dodge spam words and
        keep bodies human-length.
      </>
    ),
    facts: ['23 languages', 'ESP priority', 'own templates'],
    detail: (
      <StageCard head="Advanced controls">
        <div className="flex flex-wrap gap-1.5 font-mono text-[10.5px] md:text-[11.5px]">
          {['topics', 'language ×23', 'esp_priority', 'schedule', 'templates'].map((c) => (
            <span key={c} className="rounded border border-allow/40 bg-allow/10 px-1.5 py-0.5 text-allow">
              {c}
            </span>
          ))}
        </div>
        <div className="mt-2 font-mono text-[10.5px] text-faint md:text-[11px]">
          bans &ldquo;free / urgent / act now&rdquo; · 30–80 word bodies
        </div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'Reputation',
    title: 'Per-ESP score, synced daily',
    body: (
      <>
        Every day we pull each mailbox&rsquo;s reputation and split it by provider —{' '}
        <span className="font-mono">score_google</span>, <span className="font-mono">score_microsoft</span>,{' '}
        <span className="font-mono">score_all_other</span>, each 0–100. The same dashboard that says &ldquo;fleet
        averages 87&rdquo; flags the one inbox at 42 dragging it down.
      </>
    ),
    facts: ['google · microsoft · other', 'daily sync', 'health-check'],
    detail: (
      <StageCard head="Reputation by ESP">
        <div className="space-y-1.5">
          <Dial label="google" v={84} />
          <Dial label="microsoft" v={71} />
          <Dial label="other" v={90} />
        </div>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Warmed up',
    title: 'Crossing 80 — and staying watched',
    body: (
      <>
        The first time a mailbox crosses <span className="font-mono">80</span>,{' '}
        <span className="font-mono">warmup_inbox.warmed_up</span> fires once and sticks. A daily report webhook keeps a
        record, and a drop of 10+ points fires <span className="font-mono">warmup_inbox.reputation_dropped</span>.
      </>
    ),
    facts: ['warmed up ≥ 80', 'daily_report', 'drop ≥ 10 pts'],
    detail: (
      <StageCard head="Webhook — warmed_up">
        <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
          <code>{`{ "event": "warmup_inbox.warmed_up",
  "email": "sarah@acme.co",
  "reputationScore": 82, "threshold": 80,
  "warmedUpAt": "2026-07-30T09:12:00Z" }`}</code>
        </pre>
      </StageCard>
    ),
  },
];

export function WarmupStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How warmup works">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-allow">
          <span className="h-1.5 w-1.5 rounded-full bg-allow" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          From a cold mailbox to <span className="font-mono text-allow">warmed up</span>
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow <span className="font-mono">sarah@acme.co</span> from connect to the 80-point warmed-up mark — a gentle
          ramp, per-ESP reputation synced daily, and the webhook that tells you it&rsquo;s ready.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Warmup pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-allow to-accent transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-allow md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function Kv({ k, v, hot }: { k: string; v: string; hot?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{k}</span>
      <span className={hot ? 'font-bold text-allow' : 'text-txt'}>{v}</span>
    </div>
  );
}

function TierRow({ tier, max, reply, active }: { tier: string; max: string; reply: string; active?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between rounded border px-2 py-1 ${
        active ? 'border-allow/50 bg-allow/10 text-allow' : 'border-border text-muted'
      }`}
    >
      <span className="font-semibold">{tier}</span>
      <span>
        {max} · reply {reply}
      </span>
    </div>
  );
}

function Dial({ label, v }: { label: string; v: number }) {
  return (
    <div className="flex items-center gap-2 font-mono text-[10.5px] md:text-[11.5px]">
      <span className="w-16 text-faint">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
        <div className="h-full rounded-full bg-gradient-to-r from-allow/50 to-allow" style={{ width: `${v}%` }} />
      </div>
      <span className="w-7 text-right text-txt">{v}</span>
    </div>
  );
}
